import { z } from "zod";

import { apiGet } from "../lib/api";

/**
 * Structured evidence captured by agent tools during a call.
 *
 * Mirrors `enforcer.evidence.EvidenceItem` on the worker side and the
 * `anglerfish-call-evidence` DDB row served by
 * `GET /api/calls/{call_id}/evidence` (console-api).
 *
 * v1 tool names: `record_credential`, `record_pii`, `flag_corp_data`.
 * Kept as an open string so a new server-side tool name doesn't break
 * the UI — we surface it verbatim under an "Unknown" group heading.
 */
export const EvidenceToolNameSchema = z.union([
  z.literal("record_credential"),
  z.literal("record_pii"),
  z.literal("flag_corp_data"),
  z.string(),
]);
export type EvidenceToolName = z.infer<typeof EvidenceToolNameSchema>;

export const EvidenceItemSchema = z.object({
  evidence_id: z.string(),
  call_id: z.string(),
  engagement_id: z.string(),
  captured_at: z.string(),
  tool_name: EvidenceToolNameSchema,
  payload: z.record(z.string(), z.unknown()).default({}),
  audit_action_id: z.string(),
  turn_index: z.number().int().nullable().optional(),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

export const EvidenceResponseSchema = z.object({
  evidence: z.array(EvidenceItemSchema),
});
export type EvidenceResponse = z.infer<typeof EvidenceResponseSchema>;

export async function fetchEvidence(callId: string): Promise<EvidenceItem[]> {
  const parsed = await apiGet(
    `/api/calls/${callId}/evidence`,
    EvidenceResponseSchema,
  );
  return parsed.evidence;
}
